// Currencies with no minor unit — Medusa stores them as whole units, so no /100.
// (ISO 4217 zero-decimal list, abbreviated to the ones we plausibly bill in.)
const ZERO_DECIMAL_CURRENCIES = new Set([
  "JPY",
  "KRW",
  "VND",
  "CLP",
  "ISK",
  "HUF",
  "TWD", // Yoho doesn't actually bill in TWD, but kept defensively
  "PYG",
  "XAF",
  "XOF",
  "BIF",
  "DJF",
  "GNF",
  "KMF",
  "MGA",
  "RWF",
  "UGX",
  "VUV",
  "XPF",
])

function divisorFor(currencyCode: string): number {
  return ZERO_DECIMAL_CURRENCIES.has(currencyCode.toUpperCase()) ? 1 : 100
}

/**
 * Format a Medusa-style minor-unit amount (cents for USD/EUR/CNY,
 * whole units for JPY/KRW/etc) into a localized currency string.
 *
 * Returns "" for null/undefined/empty/non-finite input so callers can
 * short-circuit display when no amount is available.
 *
 * The amount=0 case is intentionally still formatted (e.g. "$0.00") — the
 * caller decides whether to render "Free" instead via {@link isFreeAmount}.
 */
export function formatMinorAmount(
  amountMinor: number | string | null | undefined,
  currencyCode: string | null | undefined,
  lng: string
): string {
  if (amountMinor === null || amountMinor === undefined || amountMinor === "") {
    return ""
  }
  const numeric = Number(amountMinor)
  if (!Number.isFinite(numeric)) return ""
  const currency = (currencyCode || "USD").toUpperCase()
  const amount = numeric / divisorFor(currency)
  try {
    return new Intl.NumberFormat(lng, {
      style: "currency",
      currency,
    }).format(amount)
  } catch {
    // Unknown ISO 4217 code, or unsupported locale combo — fall back to
    // "CODE 1.23" rather than throwing.
    return `${currency} ${amount.toFixed(2)}`
  }
}

/**
 * True when the input represents zero money in either major or minor units.
 * Callers can use this to render a localized "Free" label instead of
 * "$0.00", which reads better for promotional / free-claim orders.
 */
export function isFreeAmount(
  amountMinor: number | string | null | undefined
): boolean {
  if (amountMinor === null || amountMinor === undefined || amountMinor === "") {
    return false
  }
  const numeric = Number(amountMinor)
  return Number.isFinite(numeric) && numeric === 0
}
